import React, { ReactElement, useContext, useEffect, useLayoutEffect, useMemo, useState } from 'react'
import { Outlet, useLocation } from 'react-router-dom'

import { ExchangeRatesRequest } from '@api/exchangeRates'
import CookiesConsent from '@components/CookiesConsent'
import ErrorPage from '@components/ErrorPage'
import Initializer from '@components/Initializer'
import WhatsappButton from '@components/WhatsappButton'
import { useAppContext } from '@context/app'
import useCustomStyles from '@hooks/useCustomStyles'
import useSearchParams from '@hooks/useSearchParams'
import analytics from '@lib/analytics'
import cookies from '@lib/cookies'
import date from '@lib/date'
import marketingParamsUtils from '@lib/marketingParams'
import paramsUtils from '@lib/params'
import style from '@lib/style'
import { useAnalyticsSettings } from '@loaders/analyticsSettings'
import { useExchangeRatesLoader } from '@loaders/exchangeRates'
import { usePassengersTypesLoader } from '@loaders/passengerTypes'
import Metadata from '@pages/Index/Metadata'
import { defaultPluginContextValue, PluginContext } from '@plugins/index'
import PluginSlot from '@plugins/Slot'
import usePluginQuery from '@queries/plugin'
import { useSettings } from '@queries/settings'
import { useExchangeRates } from '@stores/exchangeRates'
import { useParams } from '@stores/params'
import { usePassengerTypes } from '@stores/passengerTypes'

const OutletWrapper = () => {
  const {
    hooks: { useAppSideEffect },
  } = useContext(PluginContext)

  useAppSideEffect?.()

  return <Outlet />
}

type CookiesDecision = boolean | 'pending'

const IndexPage = (): ReactElement => {
  const [settings, settingsQuery] = useSettings()
  const [queryParams] = useSearchParams()
  const [params, setParams, paramsMeta] = useParams()
  const [, setExchangeRates] = useExchangeRates()
  const [, setPassengerTypes] = usePassengerTypes()
  const { pathname } = useLocation()
  const { retailerPartnerNumber, marketingCarrierCode } = useAppContext()
  const { cookiesConsent, passengerTypesList, currency, whatsappButton } = settings
  const [analyticsInitialized, setAnalyticsInitialized] = useState(false)
  const [cookiesUserDecision, setCookiesUserDecision] = useState<CookiesDecision>(
    () => cookies.consent.get() ?? 'pending',
  )

  const isCookiesAllowed = useMemo<boolean>(() => {
    if (cookiesUserDecision !== 'pending') return cookiesUserDecision
    if (cookiesConsent.provider) return true
    return !cookiesConsent.enabled
  }, [cookiesConsent.enabled, cookiesUserDecision, cookiesConsent.provider])

  const colors = params.mode === 'page' ? settings.colors : { ...settings.colors, ...style.getEmbedBackgroundColor() }

  const analyticsSettings = useAnalyticsSettings({ retailerPartnerNumber: retailerPartnerNumber })

  const pluginQuery = usePluginQuery()

  useEffect(() => {
    if (!settingsQuery.isFetching && analyticsSettings.data && !analyticsInitialized && isCookiesAllowed) {
      analytics.init(analyticsSettings.data, { consentPolicy: cookiesConsent.enabled ? cookiesConsent : undefined })
      setAnalyticsInitialized(true)
    }
  }, [settingsQuery.isFetching, analyticsSettings.data, cookiesConsent, analyticsInitialized, isCookiesAllowed])

  const passengersTypesLoader = usePassengersTypesLoader(
    {
      enabled:
        (passengerTypesList.enabled && !passengerTypesList.multiple) || (!!params.passengers && !!params.bookingId),
      marketingCarrierCode: marketingCarrierCode ?? passengerTypesList.carrier,
    },
    {
      onSuccess: data => {
        setPassengerTypes({ types: data })
      },
    },
  )
  const passengerLoading = passengersTypesLoader.enabled
    ? passengersTypesLoader.status === 'idle' || passengersTypesLoader.status === 'loading'
    : false

  const exchangeRatesParams: ExchangeRatesRequest = {
    date: date.formatDate(new Date()),
    currencies: settingsQuery.isFetching ? null : currency.supported,
  }
  useExchangeRatesLoader(exchangeRatesParams, {
    onSuccess: rates => {
      setExchangeRates({ rates })
    },
  })

  useEffect(() => {
    const { multiple, ageRanges } = passengerTypesList

    multiple && ageRanges.length > 0 && setPassengerTypes({ types: ageRanges })
  }, [setPassengerTypes, passengerTypesList])

  useEffect(() => {
    marketingParamsUtils.initializeMarketingParams()
  }, [])

  useEffect(() => {
    if (settingsQuery.isFetching) return

    const normalisedParams = paramsUtils.normaliseParams({
      params: queryParams,
      retailerPartnerNumber,
      settings,
    })

    setParams(normalisedParams)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settingsQuery.isFetching, queryParams])

  useCustomStyles(colors)

  // Temporary solution, we wait for the params update in case we have RPN
  // !Checks if passenger types loader only required on result page then move it from Index page!
  const paramsLoading = retailerPartnerNumber > 0 ? paramsMeta.isInitialState : false
  const isPassengerLoading = pathname !== '/checkout' && passengerLoading
  const initializerProps = {
    locale: settingsQuery.isFetching || paramsLoading ? null : params.locale,
    loading: settingsQuery.isFetching || paramsLoading || isPassengerLoading || pluginQuery.isFetching,
    params: {
      retailerPartnerNumber: retailerPartnerNumber > 0 ? retailerPartnerNumber : undefined,
      marketingCarrierCode: queryParams.marketingCarrierCode,
    },
  }

  useLayoutEffect(() => {
    if (pluginQuery.data) {
      const classNames = [
        pluginQuery.data.plugin.code && `plugin-${pluginQuery.data.plugin.code}`,
        ...pluginQuery.data.abTests.map(({ code }) => `abtest__${code}`),
      ].filter((x): x is string => !!x)
      document.body.classList.add(...classNames)

      // istanbul ignore next: will be covered in the next GEB tickets
      return () => {
        document.body.classList.remove(...classNames)
      }
    }
  }, [pluginQuery.data])

  const shouldShowCookiesBanner =
    !settingsQuery.isFetching && cookiesConsent.enabled && !cookiesConsent.provider && cookiesUserDecision === 'pending'

  const changeCookiesConsent = (isAccepted: boolean) => {
    setCookiesUserDecision(isAccepted)
    cookies.consent.set(isAccepted)
  }

  return (
    <PluginContext.Provider value={pluginQuery.data ?? defaultPluginContextValue}>
      <Initializer {...initializerProps}>
        {!!settingsQuery.error || <Metadata />}
        {!!settingsQuery.error || <OutletWrapper />}
        {!!settingsQuery.error && <ErrorPage error={settingsQuery.error} />}
        <div className="floating-buttons">
          {whatsappButton.enabled && whatsappButton.phoneNumber && (
            <WhatsappButton phoneNumber={whatsappButton.phoneNumber} />
          )}
          <PluginSlot path="FloatingButtons" />
        </div>
        <CookiesConsent opened={shouldShowCookiesBanner} onChange={changeCookiesConsent} />
      </Initializer>
    </PluginContext.Provider>
  )
}

export default IndexPage
